import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { fmt$Denominated } from "../../utils/format";

/**
 * The issuer onboarding fee, on the screen where a campaign is approved.
 *
 * WHY IT EXISTS. Until now the admin console had NO fee surface of any kind — a
 * grep for fee_obligation across this whole repo returned nothing. The ledger
 * row, its reference code, its due date and its wire coordinates existed only in
 * the database, and there was no way to record that the fee had been RECEIVED:
 * campaign_fee_obligations.status has carried 'paid', 'waived' and 'cancelled'
 * since the table was created and nothing anywhere wrote any of them. Marking a
 * payment meant a hand-written SQL UPDATE.
 *
 * Schedule "A" cl.1 makes this the point of the fee — it is "due upon
 * submission", and RFI "has no obligation to commence onboarding, review,
 * listing or due diligence until it is received in full". A right to withhold is
 * unexercisable while receipt is unrecordable.
 *
 * IT GATES NOTHING. Whether approval should be blocked on a paid fee is a
 * product decision — cl.1 grants a right to withhold, it does not impose a duty
 * — and wiring it here would take that decision by accident. This panel reports
 * the fact and lets an admin record it.
 *
 * paid_at IS THE VALUE DATE ON THE BANK STATEMENT, asked for explicitly rather
 * than defaulted to today. A default would look right and quietly record the
 * wrong date on every row, which is unrecoverable once the statement is filed.
 */

const API = process.env.REACT_APP_API;
const headers = () => ({ "x-access-token": localStorage.getItem("accessToken") });

const STATUS_COPY = {
  pending_config: {
    label: "Not yet payable",
    hint:
      "The fee amount or the wire coordinates are not configured for this currency, " +
      "so the issuer cannot pay it. Check the issuer_onboarding_fee and " +
      "issuer_fee_wire_instructions config before chasing the issuer.",
  },
  pending_payment: { label: "Awaiting payment", hint: "" },
  paid: { label: "Paid", hint: "" },
  waived: { label: "Waived", hint: "" },
  cancelled: { label: "Cancelled", hint: "" },
};

const SETTLED = ["paid", "waived", "cancelled"];

const Row = ({ label, children }) => (
  <div className="detailItem">
    <span className="itemKey">{label}</span>
    <span className="itemValue">{children}</span>
  </div>
);

// Shown verbatim off the stored string. Passing a date-only value through a Date
// renders the previous day west of UTC.
const day = (v) => (v ? String(v).slice(0, 10) : "— not set");

export default function FeeObligationPanel({ campaignId, canSettle = true }) {
  const [state, setState] = useState({ loading: true, error: null, fee: null });

  const load = async () => {
    if (!campaignId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.get(
        `${API}admin/campaign-fee-obligation/${campaignId}`,
        { headers: headers() }
      );
      setState({ loading: false, error: null, fee: res?.data?.data || null });
    } catch (e) {
      // A 404 is not an error worth shouting about: plenty of campaigns predate
      // fee obligations entirely, and every one of them would otherwise show a
      // red banner on a screen an admin uses constantly.
      const missing = e?.response?.status === 404;
      setState({
        loading: false,
        error: missing ? null : e?.response?.data?.message || "Could not load the fee obligation.",
        fee: null,
      });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  const settle = async (status) => {
    const isPayment = status === "paid";
    const { value, isConfirmed } = await Swal.fire({
      title: isPayment ? "Record payment received" : `Mark this fee ${status}`,
      html:
        (isPayment
          ? '<p style="text-align:left;margin:0 0 8px">Enter the VALUE DATE of the credit on the bank statement, not today’s date.</p>' +
            '<input id="fee-paid-at" type="date" class="swal2-input" style="width:auto">'
          : "") +
        (isPayment
          ? '<p style="text-align:left;margin:12px 0 4px">Amount CREDITED, as it appears on the statement.</p>'
            + `<input id="fee-amount" class="swal2-input" style="width:auto" placeholder="${fee.amount ?? ""}">`
          : "") +
        '<input id="fee-ref" class="swal2-input" placeholder="Bank reference (optional)">' +
        '<input id="fee-note" class="swal2-input" placeholder="Note (optional)">',
      showCancelButton: true,
      confirmButtonText: isPayment ? "Record payment" : `Mark ${status}`,
      focusConfirm: false,
      preConfirm: () => {
        const paidAt = isPayment ? document.getElementById("fee-paid-at")?.value : null;
        if (isPayment && !paidAt) {
          Swal.showValidationMessage("The value date is required to record a payment.");
          return false;
        }
        // WHAT THE BANK CREDITED, sent for verification. The API compares it to
        // the obligation and refuses a mismatch with 409 amount_mismatch /
        // currency_mismatch. Until this field existed that check was INERT: the
        // service accepted expectedAmount but nothing ever supplied it, so a
        // partial credit and a payment in full closed the receivable identically.
        //
        // The placeholder shows the amount owed rather than pre-filling it. A
        // pre-filled field is a confirmation click, and a verification the admin
        // confirms without reading verifies nothing.
        const amount = isPayment ? document.getElementById("fee-amount")?.value : null;
        if (isPayment && !String(amount || "").trim()) {
          Swal.showValidationMessage("Enter the amount credited, so it can be checked against the fee.");
          return false;
        }
        return {
          paid_at: paidAt || undefined,
          expected_amount: amount || undefined,
          // The obligation's own currency, so a USD statement line recorded
          // against a CAD receivable is refused rather than silently accepted.
          expected_currency: isPayment ? fee.currency || undefined : undefined,
          reference: document.getElementById("fee-ref")?.value || undefined,
          note: document.getElementById("fee-note")?.value || undefined,
        };
      },
    });
    if (!isConfirmed || !value) return;

    try {
      await axios.post(
        `${API}admin/campaign-fee-obligation/${campaignId}/settle`,
        { status, ...value },
        { headers: headers() }
      );
      await load();
      Swal.fire({ icon: "success", title: "Recorded", timer: 1600, showConfirmButton: false });
    } catch (e) {
      // 409 covers three refusals now: already_settled (the compare-and-swap
      // losing a race), amount_mismatch and currency_mismatch (the verification
      // above). Reload on all of them so the admin sees the server's version of
      // the row rather than a bare error - for a mismatch that is the figure
      // they were supposed to be checking against.
      if (e?.response?.status === 409) await load();
      Swal.fire({
        icon: "error",
        title: e?.response?.data?.message || "Could not record the settlement.",
      });
    }
  };

  if (state.loading) return null;
  if (state.error) {
    return (
      <div className="detailItem">
        <span className="itemKey">Onboarding Fee</span>
        <span className="itemValue">{state.error}</span>
      </div>
    );
  }
  // No obligation: say so plainly. A campaign submitted after the fee shipped and
  // carrying no row is exactly what the reconciliation script hunts for, so this
  // absence is information rather than an empty state to hide.
  if (!state.fee) {
    return (
      <div className="detailItem">
        <span className="itemKey">Onboarding Fee</span>
        <span className="itemValue">No fee obligation exists for this campaign.</span>
      </div>
    );
  }

  const fee = state.fee;
  const wire = fee.wire || {};
  const copy = STATUS_COPY[fee.status] || { label: fee.status, hint: "" };
  const settled = SETTLED.includes(fee.status);

  return (
    <>
      <Row label="Onboarding Fee">
        {fmt$Denominated(fee.amount, 2, fee.currency)}
      </Row>
      <Row label="Fee Status">
        {copy.label}
        {copy.hint ? <div className="itemHint">{copy.hint}</div> : null}
      </Row>
      <Row label="Payment Reference">{fee.reference_code || "— not set"}</Row>
      <Row label="Fee Due">{day(fee.due_date)}</Row>
      {fee.paid_at ? <Row label="Received">{day(fee.paid_at)}</Row> : null}
      {fee.paid_reference ? <Row label="Bank Reference">{fee.paid_reference}</Row> : null}

      {/* The coordinates the issuer was actually given. Shown so an admin
          chasing a payment quotes the same account the issuer was told to pay
          into — CAD and USD are different accounts, and USD carries an
          intermediary leg that CAD does not. */}
      {wire.beneficiary_account ? (
        <Row label="Fee Wire">
          {[wire.bank_name, wire.beneficiary_account, wire.swift_code]
            .filter(Boolean)
            .join(" · ")}
          {wire.correspondent_bank ? (
            <div className="itemHint">
              via {wire.correspondent_bank}
              {wire.correspondent_swift ? ` (${wire.correspondent_swift})` : ""}
            </div>
          ) : null}
        </Row>
      ) : null}

      {canSettle && !settled ? (
        <div className="detailItem">
          <span className="itemKey">Record</span>
          <span className="itemValue">
            <button type="button" className="btn btn-sm btn-primary" onClick={() => settle("paid")}>
              Payment received
            </button>{" "}
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => settle("waived")}>
              Waive
            </button>{" "}
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => settle("cancelled")}>
              Cancel
            </button>
          </span>
        </div>
      ) : null}
    </>
  );
}
